import React from 'react';
import {
    ArrowRight16Regular,
    CheckboxChecked16Filled,
    CheckboxIndeterminate16Filled,
    CheckboxUnchecked16Regular,
    Dismiss16Regular,
    MoreVertical20Regular,
} from '@fluentui/react-icons';
import { ModalShell, WorkstreamOptions } from './chrome';
import {
    applyAssignedWorkstream,
    assignColumnMapping,
    bulkDeleteConfirmPrompt,
    bulkDeletePrompt,
    obsImportAssignmentIntro,
    pruneSelection,
    toggleRowSelection,
    toggleVisibleSelection,
    unmappedRequiredFields,
    visibleSelectionState,
} from './listActions';
import styles from './irlObservations2026.module.css';

export function ListCheckbox({ checked, indeterminate, onChange, label, appearance = 'row' }) {
    const state = checked ? 'checked' : indeterminate ? 'mixed' : 'unchecked';
    const Icon = checked ? CheckboxChecked16Filled : indeterminate ? CheckboxIndeterminate16Filled : CheckboxUnchecked16Regular;
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={state === 'mixed' ? 'mixed' : checked}
            aria-label={label}
            className={`${styles.listCheck} ${appearance === 'header' ? styles.listCheckHeader : ''} ${
                checked || indeterminate ? styles.listCheckVisible : ''
            }`}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onChange();
            }}>
            <Icon />
        </button>
    );
}

export function ListKebabMenu({ open, onToggle, onExportCsv, onExportPdf, onDownloadTemplate, onImport, disabled }) {
    const menuRef = React.useRef(null);
    const fileRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = event => {
            if (menuRef.current && !menuRef.current.contains(event.target)) onToggle(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open, onToggle]);

    const pickFile = () => {
        onToggle(false);
        fileRef.current?.click();
    };

    return (
        <div className={styles.menu} ref={menuRef}>
            <button
                type="button"
                className={styles.kebabBtn}
                onClick={() => onToggle(!open)}
                aria-label="List actions"
                aria-haspopup="menu"
                aria-expanded={open}
                disabled={disabled}>
                <MoreVertical20Regular />
            </button>
            {open ? (
                <div className={styles.menuPop} role="menu">
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            onToggle(false);
                            onExportCsv?.();
                        }}
                        disabled={disabled}>
                        Export CSV
                    </button>
                    {onDownloadTemplate ? (
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                onToggle(false);
                                onDownloadTemplate();
                            }}
                            disabled={disabled}>
                            Download template
                        </button>
                    ) : null}
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            onToggle(false);
                            onExportPdf?.();
                        }}
                        disabled={disabled}>
                        Export PDF
                    </button>
                    {onImport ? (
                        <button type="button" role="menuitem" onClick={pickFile} disabled={disabled}>
                            Import
                        </button>
                    ) : null}
                </div>
            ) : null}
            {onImport ? (
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className={styles.hiddenFile}
                    onChange={event => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (file) onImport(file);
                    }}
                />
            ) : null}
        </div>
    );
}

function optionValue(option) {
    return option?.id == null ? '' : String(option.id);
}

function BulkField({ field }) {
    const name = field.key;
    if (field.type === 'date') {
        return (
            <label className={styles.bulkField} htmlFor={`bulk-${name}`}>
                <span>{field.label}</span>
                <input
                    id={`bulk-${name}`}
                    name={name}
                    type="date"
                    className={styles.bulkDate}
                    value={field.value}
                    onChange={event => field.onChange(event.target.value)}
                    disabled={field.disabled}
                />
            </label>
        );
    }
    return (
        <label className={styles.bulkField} htmlFor={`bulk-${name}`}>
            <span>{field.label}</span>
            <select
                id={`bulk-${name}`}
                name={name}
                className={styles.bulkSelect}
                value={field.value}
                onChange={event => field.onChange(event.target.value)}
                disabled={field.disabled}>
                <option value="">{field.placeholder || 'No change'}</option>
                {(field.options || []).map(option => (
                    <option key={optionValue(option)} value={optionValue(option)}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

function BulkDeleteConfirm({ step, count, singular, plural, busy, onContinue, onCancel, onDelete }) {
    if (!step) return null;
    const firstStep = step === 1;
    return (
        <ModalShell width="narrow">
            <div className={styles.modalHead}>
                <h2 className={styles.modalTitle}>{firstStep ? 'Delete selected' : 'Confirm delete'}</h2>
            </div>
            <div className={styles.modalBody}>
                <p className={styles.modalIntro}>
                    {firstStep
                        ? bulkDeletePrompt(count, singular, plural)
                        : bulkDeleteConfirmPrompt(count, singular, plural)}
                </p>
            </div>
            <div className={styles.modalFoot}>
                <button type="button" className={styles.mutedBtn} onClick={onCancel} disabled={busy}>
                    Cancel
                </button>
                {firstStep ? (
                    <button type="button" className={styles.primaryBtn} onClick={onContinue} disabled={busy}>
                        Continue
                    </button>
                ) : (
                    <button type="button" className={styles.dangerBtn} onClick={onDelete} disabled={busy}>
                        Delete
                    </button>
                )}
            </div>
        </ModalShell>
    );
}

export function BulkEditBar({
    count,
    fields,
    onApply,
    onClear,
    onDelete,
    busy,
    applyDisabled,
    deleteSingular,
    deletePlural,
}) {
    const [deleteStep, setDeleteStep] = React.useState(0);

    React.useEffect(() => {
        setDeleteStep(0);
    }, [count]);

    if (!count) return null;
    return (
        <>
            <div className={styles.bulkBar}>
                <span className={styles.bulkCount}>{count} selected</span>
                <div className={styles.bulkFields}>
                    {fields.map(field => (
                        <BulkField key={field.key} field={field} />
                    ))}
                </div>
                <div className={styles.bulkActions}>
                    <button type="button" className={styles.primaryBtn} onClick={onApply} disabled={busy || applyDisabled}>
                        Apply
                    </button>
                    {onDelete ? (
                        <button type="button" className={styles.dangerBtn} onClick={() => setDeleteStep(1)} disabled={busy}>
                            Delete
                        </button>
                    ) : null}
                    <button type="button" className={styles.mutedBtn} onClick={onClear} disabled={busy} aria-label="Clear selection">
                        <Dismiss16Regular />
                        Clear
                    </button>
                </div>
            </div>
            <BulkDeleteConfirm
                step={deleteStep}
                count={count}
                singular={deleteSingular}
                plural={deletePlural}
                busy={busy}
                onContinue={() => setDeleteStep(2)}
                onCancel={() => setDeleteStep(0)}
                onDelete={() => {
                    setDeleteStep(0);
                    onDelete?.();
                }}
            />
        </>
    );
}

export function ListNotice({ message, tone = 'info' }) {
    if (!message) return null;
    return <p className={tone === 'error' ? styles.listNoticeError : styles.listNotice}>{message}</p>;
}

function fieldOptionLabel(field) {
    if (field.importDisabled) return `${field.label} (not written on import)`;
    if (field.required) return `${field.label} (required)`;
    return field.label;
}

export function CsvColumnMapper({
    fileName,
    headers,
    fields,
    mapping,
    onChange,
    onCancel,
    onConfirm,
    busy,
    entityLabel,
    workstreams,
    workstreamId,
    onWorkstreamChange,
}) {
    const missing = unmappedRequiredFields(mapping, fields);
    const missingMessage = missing.length ? `Map ${missing.map(field => field.label).join(', ')} before importing.` : '';
    const hints = (fields || []).filter(field => field.hint);
    const mappedDisabled = (fields || []).filter(field => field.importDisabled && Object.values(mapping || {}).includes(field.key));
    const needsWorkstream = workstreams != null;
    const missingWorkstream = needsWorkstream && !workstreamId;

    return (
        <ModalShell width="form">
            <div className={styles.modalHead}>
                <h2 className={styles.modalTitle}>Map CSV columns to {entityLabel} fields</h2>
                <button type="button" className={styles.modalClose} onClick={onCancel} aria-label="Close" disabled={busy}>
                    <Dismiss16Regular />
                </button>
            </div>
            <div className={styles.modalBody}>
                {fileName ? <p className={styles.mapperFile}>File: {fileName}</p> : null}
                <p className={styles.modalIntro}>
                    Each column from the file maps to one field. Title is required to create a row. Leave Id on Don&apos;t
                    import to create new rows. Mapping Id updates existing rows when the import supports it.
                </p>
                {needsWorkstream ? (
                    <div className={styles.mapperWorkstream}>
                        <label>
                            <span>Workstream</span>
                            <select
                                className={styles.mapperSelect}
                                value={workstreamId || ''}
                                aria-label="Import workstream"
                                onChange={event => onWorkstreamChange?.(event.target.value)}
                                disabled={busy || !(workstreams || []).length}>
                                {(workstreams || []).map(option => (
                                    <option key={option.id} value={String(option.id)}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <p className={styles.mapperWorkstreamHint}>
                            Empty or unmapped Workstream cells use this workstream, like the old IRL picker. A Workstream
                            name that matches a data room workstream uses that workstream. A name that is not already in
                            the data room is skipped — it is not replaced by this workstream. Folder and Tag are stored
                            as the request tag even if that folder does not exist yet.
                        </p>
                    </div>
                ) : null}
                <div className={styles.mapperTableWrap}>
                    <table className={styles.mapperTable}>
                        <thead>
                            <tr>
                                <th>CSV column</th>
                                <th aria-hidden="true" />
                                <th>Maps to</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(headers || []).map((header, index) => {
                                const selected = mapping?.[header] || '';
                                const selectedField = (fields || []).find(field => field.key === selected);
                                return (
                                    <tr key={`${header}-${index}`}>
                                        <td className={styles.mapperCsvCol}>{header}</td>
                                        <td className={styles.mapperArrow}>
                                            <ArrowRight16Regular />
                                        </td>
                                        <td>
                                            <select
                                                className={styles.mapperSelect}
                                                value={selected}
                                                aria-label={`Map ${header}`}
                                                onChange={event => onChange(assignColumnMapping(mapping, header, event.target.value))}
                                                disabled={busy}>
                                                <option value="">Don&apos;t import</option>
                                                {(fields || []).map(field => (
                                                    <option key={field.key} value={field.key} disabled={field.importDisabled}>
                                                        {fieldOptionLabel(field)}
                                                    </option>
                                                ))}
                                            </select>
                                            {selectedField?.importDisabled ? (
                                                <p className={styles.mapperRowHint}>{selectedField.hint}</p>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {missingMessage ? <p className={styles.mapperError}>{missingMessage}</p> : null}
                {hints.map(field => (
                    <p key={field.key} className={styles.mapperHint}>
                        {field.hint}
                    </p>
                ))}
                {mappedDisabled.map(field => (
                    <p key={`${field.key}-mapped`} className={styles.mapperHint}>
                        {field.label} is mapped but will not be written.
                    </p>
                ))}
            </div>
            <div className={styles.modalFoot}>
                <button type="button" className={styles.mutedBtn} onClick={onCancel} disabled={busy}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={onConfirm}
                    disabled={busy || Boolean(missing.length) || missingWorkstream}>
                    Import CSV
                </button>
            </div>
        </ModalShell>
    );
}

export function ObsImportWorkstreamModal({
    rows,
    matchedCount,
    workstreams,
    onChangeRows,
    onCancel,
    onConfirm,
    busy,
}) {
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [bulkWorkstreamId, setBulkWorkstreamId] = React.useState('');
    const assignmentRows = rows || [];
    const rowIds = React.useMemo(() => assignmentRows.map(row => String(row._row)), [assignmentRows]);
    const selection = visibleSelectionState(rowIds, selectedIds);
    const allAssigned = assignmentRows.every(row => String(row.workstreamId || '').trim());
    const canApply = Boolean(selection.selectedVisibleCount && bulkWorkstreamId);

    React.useEffect(() => {
        setSelectedIds(current => pruneSelection(current, rowIds));
    }, [rowIds]);

    const applySelected = () => {
        if (!canApply) return;
        onChangeRows?.(applyAssignedWorkstream(assignmentRows, selectedIds, bulkWorkstreamId));
    };

    return (
        <ModalShell width="form">
            <div className={styles.modalHead}>
                <h2 className={styles.modalTitle}>Assign workstreams</h2>
                <button type="button" className={styles.modalClose} onClick={onCancel} aria-label="Close" disabled={busy}>
                    <Dismiss16Regular />
                </button>
            </div>
            <div className={styles.modalBody}>
                <p className={styles.modalIntro}>{obsImportAssignmentIntro(assignmentRows.length, matchedCount)}</p>
                <div className={styles.assignBar}>
                    <label>
                        <span>Set selected to</span>
                        <select
                            className={styles.mapperSelect}
                            value={bulkWorkstreamId}
                            aria-label="Set selected to"
                            onChange={event => setBulkWorkstreamId(event.target.value)}
                            disabled={busy || !(workstreams || []).length}>
                            <option value="">Select a workstream</option>
                            <WorkstreamOptions workstreams={workstreams} valueKey="id" />
                        </select>
                    </label>
                    <button type="button" className={styles.primaryBtn} onClick={applySelected} disabled={busy || !canApply}>
                        Apply
                    </button>
                </div>
                <div className={styles.mapperTableWrap}>
                    <table className={`${styles.mapperTable} ${styles.assignTable}`}>
                        <thead>
                            <tr>
                                <th className={styles.assignCheckCol}>
                                    <ListCheckbox
                                        appearance="header"
                                        checked={selection.allVisibleSelected}
                                        indeterminate={selection.someVisibleSelected}
                                        onChange={() => setSelectedIds(toggleVisibleSelection(rowIds, selectedIds))}
                                        label="Select all observations that need a workstream"
                                    />
                                </th>
                                <th>Observation</th>
                                <th>CSV workstream</th>
                                <th>Workstream</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assignmentRows.map(row => {
                                const rowId = String(row._row);
                                const checked = selectedIds.includes(rowId);
                                const original = String(row.originalWorkstream ?? row.workstream ?? '');
                                return (
                                    <tr key={rowId}>
                                        <td className={styles.assignCheckCol}>
                                            <ListCheckbox
                                                checked={checked}
                                                onChange={() => setSelectedIds(toggleRowSelection(selectedIds, rowId))}
                                                label={`Select ${row.title || `row ${rowId}`}`}
                                            />
                                        </td>
                                        <td className={styles.assignTitle}>{row.title}</td>
                                        <td className={styles.assignOriginal}>{original}</td>
                                        <td>
                                            <select
                                                className={styles.mapperSelect}
                                                value={row.workstreamId || ''}
                                                aria-label={`Workstream for ${row.title || `row ${rowId}`}`}
                                                onChange={event =>
                                                    onChangeRows?.(
                                                        applyAssignedWorkstream(assignmentRows, [rowId], event.target.value),
                                                    )
                                                }
                                                disabled={busy || !(workstreams || []).length}>
                                                <option value="">Select a workstream</option>
                                                <WorkstreamOptions workstreams={workstreams} valueKey="id" />
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className={styles.modalFoot}>
                <button type="button" className={styles.mutedBtn} onClick={onCancel} disabled={busy}>
                    Cancel
                </button>
                <button type="button" className={styles.primaryBtn} onClick={onConfirm} disabled={busy || !allAssigned}>
                    Confirm
                </button>
            </div>
        </ModalShell>
    );
}
